/**
 * ScientificCohortView.jsx — Cohort Manifold Dynamics (Invention 9) anchor surface.
 *
 * Where it lives in the product:
 *   Build Wave Step 3 (Final Review) → expandable panel below the Burst Plan
 *
 * Why a panel and not a tab:
 *   Step 3 today is a single vertical-scroll review page (no tabs). Adding
 *   the CMD surface as a collapsible panel keeps the existing rhythm
 *   intact and lets brand managers ignore it entirely if they want to.
 *
 * Three states this component renders:
 *   1. CMD disabled by feature flag    → returns null (rendered nowhere)
 *   2. CMD enabled, brand not on Enterprise tier → locked-card with upgrade CTA
 *   3. CMD enabled + Enterprise          → full panel (manifold scatter,
 *      patch cards, recommended firing order, Run Simulation button +
 *      counter-factual result panel)
 *
 * Data sources:
 *   GET  /api/brand/waves/{wave_id}/cohort-manifold  → loaded on expand
 *   POST /api/brand/waves/{wave_id}/simulate-dynamics → fired on button click
 *
 * Backward-compat note: nothing in this component touches the legacy
 * burst-plan / RFP flow. If CMD is unavailable the rest of Step 3 looks
 * exactly as it did before Sprint 0.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Sparkles, Lock, ChevronDown, ChevronUp, Loader2, Zap,
  TrendingUp, Info, Play, ArrowRight, BookOpen,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// CMD brand accent — distinct from the existing teal/pink so the surface
// reads as "different layer" without overpowering the page.
const CMD_PURPLE      = '#A78BFA';
const CMD_PURPLE_DEEP = '#6D4DEC';
const CARD            = '#1F222A';
const CARD_DEEP       = '#181A20';
const BORDER          = '#2E3240';
const T               = '#00D4FF';

// A short, ordered palette so adjacent patches don't read as the same
// color. Used to tint scatter dots and patch cards.
const PATCH_COLORS = [
  '#A78BFA', '#22D3EE', '#F472B6', '#34D399', '#FBBF24',
  '#F87171', '#60A5FA', '#A3E635', '#F9A8D4', '#FB923C',
  '#C084FC', '#5EEAD4',
];


// ─── Top-level component ──────────────────────────────────────────────


export default function ScientificCohortView({ waveId, defaultExpanded = false }) {
  const [expanded, setExpanded]     = useState(defaultExpanded);
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);

  // Lazy-load — only hit the endpoint after the user opens the panel.
  // Keeps Step 3 fast for users who never expand.
  useEffect(() => {
    if (!expanded || !waveId || data) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios.get(`${API}/api/brand/waves/${waveId}/cohort-manifold`,
              { withCredentials: true })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => {
        if (cancelled) return;
        const msg = e?.response?.data?.detail || e?.message ||
                    'Could not load Cohort Manifold data.';
        setError(typeof msg === 'string' ? msg : 'Could not load data.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [expanded, waveId, data]);

  // No wave yet → render a "save the cohort first" inert prompt so
  // the panel still appears (good IA), just with no actionable state.
  if (!waveId) {
    return <PreSaveStub onExpand={() => setExpanded((x) => !x)} expanded={expanded} />;
  }

  return (
    <div
      className="rounded-xl overflow-hidden relative"
      style={{
        background: `linear-gradient(135deg, ${CARD}, ${CARD_DEEP})`,
        border: `1px solid ${BORDER}`,
      }}
      data-testid="scientific-cohort-view"
    >
      {/* Soft purple corner glow — signature CMD ambient lighting. */}
      <div
        className="absolute -top-12 -left-12 w-48 h-48 rounded-full opacity-25 blur-3xl pointer-events-none"
        style={{ background: `radial-gradient(circle, ${CMD_PURPLE}, transparent)` }}
      />

      <PanelHeader expanded={expanded} setExpanded={setExpanded} />

      {expanded && (
        <div className="relative px-5 pb-5">
          {loading && <LoadingState />}
          {error && !loading && <ErrorState error={error} />}
          {data && !loading && !error && (
            data.enabled
              ? <EnabledBody data={data} waveId={waveId} />
              : <LockedBody reason={data.tier_gated_reason} />
          )}
        </div>
      )}
    </div>
  );
}


// ─── Sub-components ───────────────────────────────────────────────────


function PanelHeader({ expanded, setExpanded }) {
  return (
    <button
      type="button"
      onClick={() => setExpanded((x) => !x)}
      className="relative w-full text-left px-5 py-4 flex items-start gap-3 hover:bg-white/[0.02] transition"
      data-testid="cohort-view-toggle"
    >
      <Sparkles className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: CMD_PURPLE }} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-[9px] font-black tracking-[0.2em] uppercase"
                style={{ color: CMD_PURPLE }}>
            Invention 9 · Patent Pending
          </span>
        </div>
        <p className="text-sm font-bold text-white leading-snug">
          Scientific Cohort View
        </p>
        {/* Explanatory subheading — replaces the prior tagline so a brand
            manager opening this for the first time understands exactly
            what CMD does without having to click into Support. */}
        <p className="text-[11px] text-gray-400 mt-1 leading-relaxed max-w-2xl">
          CMD groups your creators into small <span className="text-white font-semibold">teams</span> based on how
          their audiences interact, simulates how each team will <span className="text-white font-semibold">amplify the others</span>,
          and recommends the exact firing order that maximizes Pulse.
        </p>
      </div>
      <span className="text-[8px] font-black tracking-widest uppercase px-2 py-1 rounded-md flex-shrink-0 mt-0.5"
            style={{
              background: `${CMD_PURPLE}15`,
              color: CMD_PURPLE,
              border: `1px solid ${CMD_PURPLE}40`,
            }}>
        Enterprise
      </span>
      <span className="flex-shrink-0 mt-1">
        {expanded
          ? <ChevronUp className="h-4 w-4 text-gray-500" />
          : <ChevronDown className="h-4 w-4 text-gray-500" />}
      </span>
    </button>
  );
}


function PreSaveStub({ onExpand, expanded }) {
  return (
    <div
      className="rounded-xl px-5 py-4 flex items-center gap-3 opacity-70"
      style={{
        background: `linear-gradient(135deg, ${CARD}, ${CARD_DEEP})`,
        border: `1px solid ${BORDER}`,
      }}
      data-testid="scientific-cohort-view-pre-save"
    >
      <Sparkles className="h-4 w-4" style={{ color: CMD_PURPLE }} />
      <div className="flex-1">
        <p className="text-[11px] font-black uppercase tracking-wider"
           style={{ color: CMD_PURPLE }}>
          Scientific Cohort View
        </p>
        <p className="text-[11px] text-gray-400 mt-0.5">
          Generate RFPs first to unlock the manifold view of this cohort.
        </p>
      </div>
      <span className="text-[8px] font-black tracking-widest uppercase px-2 py-1 rounded-md"
            style={{
              background: `${CMD_PURPLE}15`,
              color: CMD_PURPLE,
              border: `1px solid ${CMD_PURPLE}40`,
            }}>
        Enterprise
      </span>
    </div>
  );
}


function LoadingState() {
  return (
    <div className="flex items-center gap-3 py-8 justify-center">
      <Loader2 className="h-4 w-4 animate-spin" style={{ color: CMD_PURPLE }} />
      <span className="text-xs text-gray-500">
        Analyzing cohort structure…
      </span>
    </div>
  );
}


function ErrorState({ error }) {
  return (
    <div
      className="rounded-lg px-4 py-3 text-[11px]"
      style={{
        background: 'rgba(255,107,107,0.08)',
        border: '1px solid rgba(255,107,107,0.35)',
        color: '#FF6B6B',
      }}
    >
      {error}
    </div>
  );
}


function LockedBody({ reason }) {
  return (
    <div
      className="rounded-lg p-6 text-center"
      style={{
        background: CARD_DEEP,
        border: `1px dashed ${BORDER}`,
      }}
      data-testid="scientific-cohort-view-locked"
    >
      <Lock className="h-6 w-6 mx-auto mb-3" style={{ color: CMD_PURPLE }} />
      <p className="text-sm font-bold text-white mb-2">
        Unlock the science behind your waves
      </p>
      <p className="text-[12px] text-gray-400 max-w-md mx-auto mb-5 leading-relaxed">
        {reason || 'Cohort Mapping predicts how your creators will fire together — and recommends the exact firing order most likely to lift Pulse, before a single post goes live.'}
      </p>
      <Link
        to="/billing"
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-[11px] font-black uppercase tracking-wider"
        style={{
          background: `linear-gradient(135deg, ${CMD_PURPLE}, ${CMD_PURPLE_DEEP})`,
          color: '#0A0A0A',
        }}
        data-testid="cmd-upgrade-cta"
      >
        Upgrade to Enterprise <ArrowRight className="h-3 w-3" />
      </Link>
      <Link
        to="/help/cmd"
        className="block mt-3 text-[10px] text-gray-500 hover:text-gray-300"
      >
        <BookOpen className="inline h-3 w-3 mr-1" />
        What is Cohort Mapping?
      </Link>
    </div>
  );
}


function EnabledBody({ data, waveId }) {
  const patches = data.patches || [];
  const manifold = data.manifold || { creator_coords: {}, explained_var: 0 };
  const couplingPct = data.coupling_strength_pct || 0;
  const rationale = data.firing_order_rationale || '';

  // We hoist the simulation result up to this scope so the headline +
  // narration above the scatter can refresh once the user clicks
  // "Run CMD Simulation". Without this hoist, the headline reads
  // "no measurable lift" forever even after the sim returns +N.
  const [simResult, setSimResult] = useState(null);
  const effectiveDelta = simResult?.projected_delta ?? 0;
  const headline = simResult
    ? (simResult.narration?.body || data.headline)
    : data.headline;

  // We no longer build a scatter dataset — the network panel reads
  // patches + coupling_matrix directly. Kept this comment so reviewers
  // notice the manifold scatter was intentionally removed (it was
  // analytically empty for a brand manager — 140 dots, no insight).
  void manifold;  // silence unused-var lint while leaving the prop wired

  return (
    <div className="space-y-4">
      {/* ─── Headline narration ──────────────────────────────────── */}
      <div className="flex items-start gap-3 px-4 py-3 rounded-lg"
           style={{ background: CARD_DEEP, border: `1px solid ${BORDER}` }}>
        <Info className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: CMD_PURPLE }} />
        <div className="flex-1 min-w-0">
          {simResult && effectiveDelta > 0 && (
            <p className="text-[10px] font-black uppercase tracking-widest mb-1"
               style={{ color: CMD_PURPLE }}>
              ▲ +{effectiveDelta} Pulse projected with CMD applied
            </p>
          )}
          <p className="text-[12px] text-white leading-snug font-medium">
            {headline}
          </p>
          {rationale && (
            <p className="text-[11px] text-gray-400 leading-snug mt-1.5">
              {rationale}
            </p>
          )}
        </div>
      </div>

      {/* ─── Patch network + sidebar (2-column) ────────────────────
          The network graph replaces the prior scatter plot — instead of
          showing 140 abstract dots, it shows which teams couple with
          which other teams (the actual "physics" the brand manager
          cares about: who drives whom). */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
        <div className="lg:col-span-3 rounded-lg p-3"
             style={{ background: CARD_DEEP, border: `1px solid ${BORDER}` }}>
          <NetworkPanel
            patches={patches}
            couplingMatrix={data.coupling_matrix || {}}
            recommendedOrder={simResult?.recommended_order || []}
          />
        </div>
        <div className="lg:col-span-2 space-y-2">
          <p className="text-[9px] font-black uppercase tracking-widest text-gray-500 px-1">
            Detected Teams · {patches.length}
          </p>
          <div className="space-y-2 max-h-[320px] overflow-y-auto no-scrollbar pr-1">
            {patches.slice(0, 12).map((p, idx) => (
              <PatchCard
                key={p.patch_id}
                patch={p}
                color={PATCH_COLORS[idx % PATCH_COLORS.length]}
              />
            ))}
          </div>
          {patches.length > 12 && (
            <p className="text-[10px] text-gray-600 px-1 pt-1">
              + {patches.length - 12} smaller teams (collapsed)
            </p>
          )}
        </div>
      </div>

      {/* ─── Coupling line + Simulation CTA + result panel ────────── */}
      <SimulationPanel
        waveId={waveId}
        couplingPct={couplingPct}
        baselinePulse={data.baseline_pulse_hint || null}
        onResult={setSimResult}
      />
    </div>
  );
}


function NetworkPanel({ patches, couplingMatrix, recommendedOrder }) {
  // Top 8 patches by member count — keeps the network readable. We
  // surface every patch in the sidebar; the network is the "headline
  // diagram" so showing all 12 would clutter it.
  const visible = useMemo(
    () => patches.slice(0, 8),
    [patches],
  );

  const NODE_RADIUS_MIN = 22;
  const NODE_RADIUS_MAX = 38;
  const WIDTH  = 480;
  const HEIGHT = 340;
  const CENTER_X = WIDTH / 2;
  const CENTER_Y = HEIGHT / 2;

  // Scale node radius linearly with member count across the visible set.
  const counts = visible.map((p) => (p.creator_ids || []).length);
  const minCount = Math.min(...(counts.length ? counts : [1]));
  const maxCount = Math.max(...(counts.length ? counts : [1]));
  const radiusFor = (count) => {
    if (maxCount === minCount) return (NODE_RADIUS_MIN + NODE_RADIUS_MAX) / 2;
    const t = (count - minCount) / (maxCount - minCount);
    return NODE_RADIUS_MIN + t * (NODE_RADIUS_MAX - NODE_RADIUS_MIN);
  };

  // Circular layout — patches arrayed around a circle. If a recommended
  // firing order is available, sort visible by that order so the visual
  // flow matches the recommendation. Otherwise sort by patch_id.
  const orderedPatches = useMemo(() => {
    if (recommendedOrder && recommendedOrder.length) {
      const orderMap = new Map(recommendedOrder.map((id, i) => [id, i]));
      return [...visible].sort((a, b) => {
        const oa = orderMap.has(a.patch_id) ? orderMap.get(a.patch_id) : 999;
        const ob = orderMap.has(b.patch_id) ? orderMap.get(b.patch_id) : 999;
        return oa - ob;
      });
    }
    return [...visible].sort((a, b) => a.patch_id.localeCompare(b.patch_id));
  }, [visible, recommendedOrder]);

  const nodePositions = useMemo(() => {
    const n = orderedPatches.length;
    if (!n) return {};
    const radius = Math.min(WIDTH, HEIGHT) / 2 - NODE_RADIUS_MAX - 12;
    const out = {};
    orderedPatches.forEach((p, i) => {
      // Start at -π/2 (top) and go clockwise.
      const angle = -Math.PI / 2 + (i / n) * 2 * Math.PI;
      out[p.patch_id] = {
        x: CENTER_X + radius * Math.cos(angle),
        y: CENTER_Y + radius * Math.sin(angle),
        radius: radiusFor((p.creator_ids || []).length),
        color: PATCH_COLORS[patches.findIndex((q) => q.patch_id === p.patch_id) % PATCH_COLORS.length],
        patch: p,
      };
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderedPatches, patches]);

  // Edges — only between visible patches with non-zero coupling.
  const edges = useMemo(() => {
    const out = [];
    orderedPatches.forEach((p) => {
      const couplings = couplingMatrix[p.patch_id] || {};
      Object.entries(couplings).forEach(([targetId, strength]) => {
        if (!nodePositions[targetId]) return;
        if (strength <= 0) return;
        // De-dup: only render edge once per pair (lexically smaller first)
        if (p.patch_id < targetId) {
          out.push({
            source: p.patch_id, target: targetId, strength,
          });
        }
      });
    });
    return out;
  }, [orderedPatches, couplingMatrix, nodePositions]);

  if (!orderedPatches.length) {
    return (
      <div className="h-64 flex items-center justify-center text-[11px] text-gray-500">
        Not enough teams to render the coupling network.
      </div>
    );
  }

  // Find the strongest edge so we can normalize stroke width.
  const maxStrength = Math.max(0.01, ...edges.map((e) => e.strength));

  return (
    <div>
      <div className="flex items-center justify-between mb-2 px-2">
        <div>
          <p className="text-[10px] font-black uppercase tracking-widest text-gray-300">
            Team Coupling Map
          </p>
          <p className="text-[10px] text-gray-500 mt-0.5">
            Lines show how strongly teams will boost each other when they post together.
          </p>
        </div>
        <span className="text-[9px] text-gray-600 font-mono">
          {orderedPatches.length} of {patches.length} teams
        </span>
      </div>

      <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
           preserveAspectRatio="xMidYMid meet"
           className="w-full h-72"
           role="img"
           aria-label="Patch coupling network">
        {/* Soft ambient circle behind the nodes for visual depth. */}
        <circle
          cx={CENTER_X}
          cy={CENTER_Y}
          r={Math.min(WIDTH, HEIGHT) / 2 - 6}
          fill="none"
          stroke={BORDER}
          strokeDasharray="3 4"
          strokeWidth="1"
          opacity="0.35"
        />

        {/* Edges first so nodes render on top. */}
        {edges.map((e) => {
          const a = nodePositions[e.source];
          const b = nodePositions[e.target];
          if (!a || !b) return null;
          const widthPx = 1 + (e.strength / maxStrength) * 5;
          const opacity = 0.3 + 0.6 * (e.strength / maxStrength);
          return (
            <g key={`${e.source}-${e.target}`}>
              <line
                x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                stroke={CMD_PURPLE}
                strokeWidth={widthPx}
                strokeOpacity={opacity}
                strokeLinecap="round"
              />
              {/* Coupling percentage label at midpoint. */}
              <text
                x={(a.x + b.x) / 2}
                y={(a.y + b.y) / 2 - 4}
                textAnchor="middle"
                fontSize="9"
                fontWeight="700"
                fill="#9CA3AF"
                opacity={opacity + 0.1}
                style={{ pointerEvents: 'none' }}
              >
                {Math.round(e.strength * 100)}%
              </text>
            </g>
          );
        })}

        {/* Nodes — each patch as a labeled circle. */}
        {orderedPatches.map((p, idx) => {
          const pos = nodePositions[p.patch_id];
          if (!pos) return null;
          const isRecommendedFirst = recommendedOrder && recommendedOrder[0] === p.patch_id;
          return (
            <g key={p.patch_id}>
              {isRecommendedFirst && (
                <circle
                  cx={pos.x} cy={pos.y}
                  r={pos.radius + 6}
                  fill="none"
                  stroke={CMD_PURPLE}
                  strokeWidth="1.5"
                  strokeDasharray="4 3"
                  opacity="0.85"
                >
                  <animate
                    attributeName="r"
                    values={`${pos.radius + 4};${pos.radius + 8};${pos.radius + 4}`}
                    dur="2.2s"
                    repeatCount="indefinite"
                  />
                </circle>
              )}
              <circle
                cx={pos.x} cy={pos.y} r={pos.radius}
                fill={`${pos.color}25`}
                stroke={pos.color}
                strokeWidth="1.5"
              />
              <text
                x={pos.x} y={pos.y - 2}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={pos.radius >= 30 ? 16 : 14}
                fontWeight="900"
                fill={pos.color}
              >
                {p.patch_id}
              </text>
              <text
                x={pos.x} y={pos.y + 10}
                textAnchor="middle"
                fontSize="8"
                fill="#9CA3AF"
                fontWeight="600"
              >
                {(p.creator_ids || []).length}
              </text>
              {idx === 0 && recommendedOrder && recommendedOrder.length > 0 && (
                <text
                  x={pos.x}
                  y={pos.y + pos.radius + 14}
                  textAnchor="middle"
                  fontSize="9"
                  fontWeight="800"
                  fill={CMD_PURPLE}
                  letterSpacing="1"
                >
                  FIRE FIRST
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {/* Legend strip — explains line thickness and node size. */}
      <div className="flex items-center justify-center gap-5 mt-2 text-[9px] text-gray-500">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-4 h-[3px] rounded-full"
                style={{ background: CMD_PURPLE, opacity: 0.85 }} />
          thicker = stronger coupling
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-full"
                style={{ background: `${CMD_PURPLE}30`, border: `1px solid ${CMD_PURPLE}` }} />
          bigger = more creators
        </span>
      </div>
    </div>
  );
}


function PatchCard({ patch, color }) {
  return (
    <div
      className="rounded-lg p-3 flex items-center gap-3"
      style={{ background: CARD_DEEP, border: `1px solid ${BORDER}` }}
      data-testid={`patch-card-${patch.patch_id}`}
    >
      <span
        className="flex items-center justify-center w-7 h-7 rounded-md flex-shrink-0 font-black text-[12px]"
        style={{
          background: `${color}20`,
          color,
          border: `1px solid ${color}40`,
        }}
      >
        {patch.patch_id}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-bold text-white truncate">
          {patch.role_label}
        </p>
        <p className="text-[10px] text-gray-500 mt-0.5">
          {(patch.creator_ids || []).length} creators
          {patch.neighbors && patch.neighbors.length > 0 && (
            <span className="ml-1 text-gray-600">
              · couples with {patch.neighbors.slice(0, 3).join(', ')}
              {patch.neighbors.length > 3 && '…'}
            </span>
          )}
        </p>
      </div>
    </div>
  );
}


function SimulationPanel({ waveId, couplingPct, baselinePulse, onResult }) {
  const [running, setRunning]   = useState(false);
  const [result, setResult]     = useState(null);
  const [error, setError]       = useState(null);

  const runSim = useCallback(async () => {
    if (running || !waveId) return;
    setRunning(true);
    setError(null);
    try {
      const body = baselinePulse ? { baseline_pulse: baselinePulse } : {};
      const r = await axios.post(
        `${API}/api/brand/waves/${waveId}/simulate-dynamics`,
        body,
        { withCredentials: true },
      );
      setResult(r.data);
      onResult?.(r.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Simulation failed.';
      setError(typeof msg === 'string' ? msg : 'Simulation failed.');
    } finally {
      setRunning(false);
    }
  }, [waveId, baselinePulse, running, onResult]);

  return (
    <div className="rounded-lg p-4"
         style={{ background: CARD_DEEP, border: `1px solid ${BORDER}` }}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Zap className="h-3.5 w-3.5 flex-shrink-0" style={{ color: CMD_PURPLE }} />
          <p className="text-[11px] text-gray-300 leading-snug">
            <span className="font-bold text-white">
              {Math.round(couplingPct)}% cross-team coupling
            </span>
            <span className="text-gray-500">
              {' '}detected. Simulate the recommended firing order to see the
              projected Pulse lift.
            </span>
          </p>
        </div>
        <button
          type="button"
          onClick={runSim}
          disabled={running || !waveId}
          data-testid="run-cmd-simulation-btn"
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider disabled:opacity-50 transition hover:brightness-110"
          style={{
            background: `linear-gradient(135deg, ${CMD_PURPLE}, ${CMD_PURPLE_DEEP})`,
            color: '#0A0A0A',
            boxShadow: `0 0 22px ${CMD_PURPLE}40`,
          }}
        >
          {running
            ? <><Loader2 className="h-3 w-3 animate-spin" /> Simulating…</>
            : <><Play className="h-3 w-3" /> Run CMD Simulation</>}
        </button>
      </div>

      {error && (
        <p className="mt-3 text-[10px] text-amber-400">{error}</p>
      )}

      {result && (
        <SimulationResult result={result} />
      )}
    </div>
  );
}


function SimulationResult({ result }) {
  const n = result.narration || {};
  const positiveLift = (result.projected_delta || 0) > 0;
  const order = result.recommended_order || [];

  return (
    <div
      className="mt-3 pt-4 border-t flex flex-col sm:flex-row gap-4 sm:items-center"
      style={{ borderColor: BORDER }}
      data-testid="cmd-simulation-result"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <TrendingUp className="h-3.5 w-3.5" style={{ color: positiveLift ? CMD_PURPLE : '#9CA3AF' }} />
          <p className="text-[15px] font-black text-white">
            {n.headline || `Pulse ${result.baseline_pulse} → ${result.projected_pulse}`}
          </p>
        </div>
        <p className="text-[11px] text-gray-400 leading-snug">
          {n.body}
        </p>
        {order.length > 0 && (
          <p className="text-[10px] text-gray-500 mt-2">
            Recommended firing order: {order.slice(0, 6).map((id) => (
              <span key={id} className="font-mono mr-1">{id}</span>
            ))}
            {order.length > 6 && <span className="text-gray-600">+{order.length - 6} more</span>}
          </p>
        )}
      </div>
      {n.dollar_chip && (
        <div
          className="flex-shrink-0 px-3 py-2 rounded-lg text-[11px] font-black"
          style={{
            background: `${CMD_PURPLE}15`,
            color: CMD_PURPLE,
            border: `1px solid ${CMD_PURPLE}40`,
          }}
        >
          {n.dollar_chip}{' '}
          <span className="text-[9px] uppercase tracking-wider text-gray-500 font-bold">
            est. revenue lift
          </span>
        </div>
      )}
    </div>
  );
}
